import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    MenuItem,
    Paper,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    knowledgeApi,
    KnowledgeEntrySummary,
    KnowledgeIndexStatus,
    KnowledgeListResponse,
} from '../../../api/knowledgeApi';
import { AddKnowledgeModal } from './AddKnowledgeModal';
import { KnowledgeDetailDrawer } from './KnowledgeDetailDrawer';
import { KnowledgeSearchPreview } from './KnowledgeSearchPreview';
import { SettingsContext } from '../../../context/SettingsContext';

/**
 * Knowledge tab — admin CRUD for the RAG knowledge base.
 *
 * Layout (top to bottom):
 *   - Search-preview box (admin QA: "what would the LLM see for this query?")
 *   - Filters row (title + status)
 *   - Entry list (paginated)
 *   - Add-entry modal (paste or file upload)
 *   - Detail drawer (reindex / delete / chunk preview)
 */
export const KnowledgeTab: React.FC = () => {
    const settings = useContext(SettingsContext);

    const [entries, setEntries] = useState<KnowledgeEntrySummary[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [titleFilter, setTitleFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState<KnowledgeIndexStatus | ''>('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [addOpen, setAddOpen] = useState(false);
    const [detailId, setDetailId] = useState<string | null>(null);
    const [snack, setSnack] = useState<string | null>(null);

    const fetchEntries = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const resp: KnowledgeListResponse = await knowledgeApi.list({
                page: page + 1, // backend is 1-indexed
                limit: rowsPerPage,
                indexStatus: statusFilter || undefined,
                titleContains: titleFilter.trim() || undefined,
            });
            setEntries(resp.entries);
            setTotal(resp.total);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load knowledge entries');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, statusFilter, titleFilter]);

    useEffect(() => {
        fetchEntries();
    }, [fetchEntries]);

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this knowledge entry? Chunks cascade and the worker bot will no longer use this content.')) {
            return;
        }
        try {
            await knowledgeApi.delete(id);
            setSnack('Entry deleted');
            await fetchEntries();
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Delete failed');
        }
    };

    const handleReindex = async (id: string) => {
        // URL entries re-FETCH the source page on reindex; FILE / PASTE just
        // re-chunk the stored snapshot. Surface the difference so admin knows
        // a URL reindex can hit the network (and therefore can robots-fail
        // or 4xx if the page moved).
        const entry = entries.find((e) => e.id === id);
        const isUrl = entry?.sourceType === 'URL';
        try {
            await knowledgeApi.reindex(id);
            setSnack(isUrl ? 'Re-fetching URL…' : 'Reindex started');
            await fetchEntries();
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Reindex failed');
        }
    };

    const professionOptions = useMemo(
        () => settings?.settings?.professions?.map((p: any) => ({ key: p.professionKey, label: p.professionName })) ?? [],
        [settings],
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
                <Typography variant="h5" sx={{ mb: 0.5 }}>
                    Knowledge Base
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Admin-curated content the bot uses to answer worker questions about Optima, professions, the recruitment process, payment, certifications, etc.
                </Typography>
            </Box>

            <KnowledgeSearchPreview professionOptions={professionOptions} />

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flex: 1 }}>
                        <TextField
                            label="Filter by title"
                            value={titleFilter}
                            onChange={(e) => { setTitleFilter(e.target.value); setPage(0); }}
                            size="small"
                            sx={{ minWidth: 220 }}
                        />
                        <TextField
                            select
                            label="Status"
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value as KnowledgeIndexStatus | ''); setPage(0); }}
                            size="small"
                            sx={{ minWidth: 160 }}
                        >
                            <MenuItem value="">(any)</MenuItem>
                            <MenuItem value="PENDING">Pending</MenuItem>
                            <MenuItem value="INDEXING">Indexing</MenuItem>
                            <MenuItem value="READY">Ready</MenuItem>
                            <MenuItem value="FAILED">Failed</MenuItem>
                        </TextField>
                    </Stack>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
                        Add entry
                    </Button>
                </Stack>

                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Title</TableCell>
                                <TableCell>Source</TableCell>
                                <TableCell>Scope</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Chunks</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && entries.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No entries yet. Click <b>Add entry</b> to upload a file or paste text.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && entries.map((e) => (
                                <TableRow key={e.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{e.title}</Typography>
                                        {e.sourceFilename && (
                                            <Typography variant="caption" color="text.secondary">{e.sourceFilename}</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={e.sourceType} />
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                            <Chip size="small" label={e.scope?.audience ?? 'WORKER'} variant="outlined" />
                                            {(e.scope?.profession ?? []).map((p) => (
                                                <Chip key={p} size="small" label={p} variant="outlined" color="primary" />
                                            ))}
                                            {(e.scope?.language ?? []).map((l) => (
                                                <Chip key={l} size="small" label={l} variant="outlined" color="secondary" />
                                            ))}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <StatusChip status={e.indexStatus} errorMessage={e.indexErrorMessage} />
                                    </TableCell>
                                    <TableCell align="right">{e.chunkCount}</TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{new Date(e.createdAt).toLocaleString()}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="View / preview chunks"><IconButton size="small" onClick={() => setDetailId(e.id)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Reindex"><IconButton size="small" onClick={() => handleReindex(e.id)}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(e.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>

            <AddKnowledgeModal
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onCreated={async () => { setSnack('Entry created'); setAddOpen(false); await fetchEntries(); }}
                professionOptions={professionOptions}
            />

            <KnowledgeDetailDrawer
                entryId={detailId}
                onClose={() => setDetailId(null)}
                onReindex={async (id) => { await handleReindex(id); }}
                onDelete={async (id) => { await handleDelete(id); setDetailId(null); }}
            />

            <Snackbar
                open={!!snack}
                autoHideDuration={3000}
                onClose={() => setSnack(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setSnack(null)}>{snack}</Alert>
            </Snackbar>
        </Box>
    );
};

const StatusChip: React.FC<{ status: KnowledgeIndexStatus; errorMessage: string | null }> = ({ status, errorMessage }) => {
    const color = status === 'READY' ? 'success' : status === 'FAILED' ? 'error' : status === 'INDEXING' ? 'warning' : 'default';
    const chip = <Chip size="small" label={status} color={color as any} variant={status === 'READY' ? 'filled' : 'outlined'} />;
    if (status === 'FAILED' && errorMessage) {
        return <Tooltip title={errorMessage} arrow>{chip}</Tooltip>;
    }
    return chip;
};
